/**
 * Field/relation edit flows for a single populated repository entry (scalar dialog, belongs-to
 * block dialog, and linked “add entry” create). Reuse from `RepositoryEntryView`, `RepositoryEntryDrawer`, etc.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
	BelongsToPopulatedValue,
	EntityType,
	PopulatedEntry,
	PropertyDefinition,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
	RepositoryEntry,
} from '../types';
import { useRepositoryEntryDialog } from '../components/AddRepositoryEntryDialog';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { repositoryEntryUpdateLocalAction, selectEntryReadableValues } from '../containers/contentSlice';
import { RepositoryEntriesRowDto, useRepositoryEntryUpdateMutation } from '../services/repositoryEntries';
import type { EditRepositoryEntryFieldDialogProps } from '../components/EditRepositoryEntryFieldDialog';
import type { RelationValue } from '../components/EntryPropertyBelongsToEditDialog';
import type { UseRepositoryEntryDrawerChangesResult } from './useRepositoryEntryDrawerChanges';
import type { RootState } from '../store';

export type RepositoryEntryEditRelationDialogProps = {
	def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	values: RelationValue[];
	onNavigateToEntry: (entryId: string) => void;
	onAddEntryClick: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => void;
	onChangeValues: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, nextValues: RelationValue[]) => void;
	open: boolean;
	onClose: () => void;
	disabled: boolean;
};

export type UseRepositoryEntryEditFlowArgs = {
	activeEntry: PopulatedEntry | null;
	repositoryEntryDialog: ReturnType<typeof useRepositoryEntryDialog>;
	onNavigateToEntry: (entryId: string) => void;
	/** Clears field/relation/add-link state when this identity changes (e.g. route location or drawer entry). */
	resetKey?: string | null;
	isAnyDialogOpen: boolean;
	/** When given, linking a newly created related entry is held as an unsaved drawer change instead of being saved right away. */
	drawerChanges?: UseRepositoryEntryDrawerChangesResult;
};

export type UseRepositoryEntryEditFlowResult = {
	anyBlockingDialog: boolean;
	/** Field or relation editor from this flow (excludes `isAnyDialogOpen` and add-entry dialog). */
	hasFieldOrRelationDialog: boolean;
	onRelationDialogOpen: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas) => void;
	/** Opens the add-entry dialog; the created entry gets linked to the active entry through `def`. */
	onAddRelatedEntryClick: (def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => void;
	onScalarFieldEdit: (entry: PopulatedEntry, def: PropertyDefinition) => void;
	closeFieldEditDialog: () => void;
	notifyAddRepositoryEntryCreated: (createdEntry: RepositoryEntry) => boolean;
	closeAddRepositoryEntryDialogWithCleanup: () => void;
	editFieldDialogProps: Pick<
		EditRepositoryEntryFieldDialogProps,
		'open' | 'onClose' | 'entry' | 'propertyDefinition' | 'onSubmitValues' | 'onNavigateToEntry' | 'onAddEntryClick' | 'disabled'
	>;
	relationDialogProps: RepositoryEntryEditRelationDialogProps | null;
};

export function useRepositoryEntryEditFlow({
	activeEntry,
	repositoryEntryDialog,
	onNavigateToEntry,
	resetKey,
	isAnyDialogOpen,
	drawerChanges,
}: UseRepositoryEntryEditFlowArgs): UseRepositoryEntryEditFlowResult {
	const dispatch = useDispatch();
	const store = useStore<RootState>();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { openAddRepositoryEntryDialog, closeAddRepositoryEntryDialog } = repositoryEntryDialog;

	const [fieldEditTarget, setFieldEditTarget] = useState<{ entry: PopulatedEntry; def: PropertyDefinition } | null>(null);
	const [pendingForeignCreateLink, setPendingForeignCreateLink] = useState<{
		entryId: string;
		def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
		currentEntryIds: string[];
	} | null>(null);
	const [relationDialogState, setRelationDialogState] = useState<{
		def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	} | null>(null);

	const [repositoryEntryUpdateMutation] = useRepositoryEntryUpdateMutation();

	useEffect(() => {
		setFieldEditTarget(null);
		setRelationDialogState(null);
		setPendingForeignCreateLink(null);
	}, [resetKey]);

	const hasFieldOrRelationDialog = useMemo(
		() => Boolean(fieldEditTarget) || Boolean(relationDialogState),
		[fieldEditTarget, relationDialogState],
	);

	const anyBlockingDialog = useMemo(
		() => isAnyDialogOpen || hasFieldOrRelationDialog,
		[isAnyDialogOpen, hasFieldOrRelationDialog],
	);

	const closeFieldEditDialog = useCallback(() => setFieldEditTarget(null), []);

	const handleFieldEditSubmitValues = useCallback(
		(values: Record<string, unknown>) => {
			if (!fieldEditTarget) {
				return;
			}
			const id = fieldEditTarget.entry.id;
			if (isAuthenticated) {
				repositoryEntryUpdateMutation({ id, values });
			} else {
				dispatch(repositoryEntryUpdateLocalAction({ id, values }));
			}
		},
		[fieldEditTarget, isAuthenticated, repositoryEntryUpdateMutation, dispatch],
	);

	const handleScalarFieldEdit = useCallback((entry: PopulatedEntry, def: PropertyDefinition) => {
		setFieldEditTarget({ entry, def });
	}, []);

	const handleAddEntryClick = useCallback(
		(def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, currentEntryIds: string[]) => {
			if (!activeEntry) {
				return;
			}
			setPendingForeignCreateLink({
				entryId: activeEntry.id as string,
				def,
				currentEntryIds,
			});
			openAddRepositoryEntryDialog({
				repositoryId: def.targetRepositoryId,
				linkToProperty: def,
				linkToEntryId: activeEntry.id as string,
			});
		},
		[activeEntry, openAddRepositoryEntryDialog],
	);

	const applyPendingForeignCreateLink = useCallback(
		(createdEntry: RepositoryEntry, link: NonNullable<typeof pendingForeignCreateLink>) => {
			const { entryId, def, currentEntryIds } = link;
			const isSingleValue = def.type === EntityType.PropertyDefinitionBelongsTo && !def.allowMany;
			if (drawerChanges && activeEntry) {
				const createdValue = {
					value: createdEntry.id,
					label: selectEntryReadableValues(store.getState()).get(createdEntry.id) ?? '(no label)',
				};
				const currentValues = drawerChanges.getRelationValues(activeEntry, def).filter(({ value }) => value !== createdEntry.id);
				drawerChanges.setPendingRelationValues(entryId, def, isSingleValue ? [createdValue] : [...currentValues, createdValue]);
				setPendingForeignCreateLink(null);
				return;
			}
			const uniqueIds = Array.from(new Set([...currentEntryIds, createdEntry.id]));
			const nextValues = isSingleValue ? [createdEntry.id] : uniqueIds;
			const valuesPayload = { [def.slug]: nextValues };
			if (isAuthenticated) {
				repositoryEntryUpdateMutation({ id: entryId, values: valuesPayload });
			} else {
				dispatch(repositoryEntryUpdateLocalAction({ id: entryId, values: valuesPayload }));
			}
			setPendingForeignCreateLink(null);
		},
		[drawerChanges, activeEntry, store, isAuthenticated, repositoryEntryUpdateMutation, dispatch],
	);

	const notifyAddRepositoryEntryCreated = useCallback(
		(createdEntry: RepositoryEntry) => {
			if (!pendingForeignCreateLink) {
				return false;
			}
			applyPendingForeignCreateLink(createdEntry, pendingForeignCreateLink);
			return true;
		},
		[pendingForeignCreateLink, applyPendingForeignCreateLink],
	);

	const closeAddRepositoryEntryDialogWithCleanup = useCallback(() => {
		setPendingForeignCreateLink(null);
		closeAddRepositoryEntryDialog();
	}, [closeAddRepositoryEntryDialog]);

	const handleRelationValuesChange = useCallback(
		(def: PropertyDefinitionBelongsTo | PropertyDefinitionHas, nextValues: RelationValue[]) => {
			if (!activeEntry) {
				return;
			}
			const valuesPayload = { [def.slug]: nextValues.map(({ value }) => value) };
			if (isAuthenticated) {
				repositoryEntryUpdateMutation({ id: activeEntry.id as string, values: valuesPayload, optimisticValues: { [def.slug]: nextValues } });
			} else {
				dispatch(repositoryEntryUpdateLocalAction({ id: activeEntry.id as string, values: valuesPayload }));
			}
		},
		[activeEntry, isAuthenticated, repositoryEntryUpdateMutation, dispatch],
	);

	const currentRelationValues = useMemo((): RelationValue[] => {
		if (!relationDialogState || !activeEntry) {
			return [];
		}
		const data = activeEntry.data;
		const values = data[relationDialogState.def.slug] ?? [];
		return Array.isArray(values) ? (values as BelongsToPopulatedValue[]) : [];
	}, [relationDialogState, activeEntry]);

	const relationDialogProps = useMemo((): RepositoryEntryEditRelationDialogProps | null => {
		if (!relationDialogState || !activeEntry) {
			return null;
		}
		return {
			def: relationDialogState.def,
			values: currentRelationValues,
			onNavigateToEntry,
			onAddEntryClick: handleAddEntryClick,
			onChangeValues: handleRelationValuesChange,
			open: true,
			onClose: () => setRelationDialogState(null),
			disabled: Boolean(activeEntry.isArchived),
		};
	}, [relationDialogState, activeEntry, currentRelationValues, onNavigateToEntry, handleAddEntryClick, handleRelationValuesChange]);

	const editFieldDialogProps = useMemo(
		(): UseRepositoryEntryEditFlowResult['editFieldDialogProps'] => ({
			open: Boolean(fieldEditTarget),
			onClose: closeFieldEditDialog,
			entry: (fieldEditTarget?.entry ?? null) as RepositoryEntriesRowDto | null,
			propertyDefinition: fieldEditTarget?.def ?? null,
			onSubmitValues: handleFieldEditSubmitValues,
			onNavigateToEntry,
			onAddEntryClick: (def, currentEntryIds) => {
				const linkToEntryId = fieldEditTarget?.entry.id;
				const sourceEntryId = fieldEditTarget?.entry.id;
				closeFieldEditDialog();
				if (linkToEntryId && sourceEntryId) {
					setPendingForeignCreateLink({
						entryId: sourceEntryId,
						def,
						currentEntryIds,
					});
					openAddRepositoryEntryDialog({
						repositoryId: def.targetRepositoryId,
						linkToProperty: def,
						linkToEntryId,
					});
				}
			},
			disabled: Boolean(fieldEditTarget?.entry.isArchived),
		}),
		[fieldEditTarget, closeFieldEditDialog, handleFieldEditSubmitValues, onNavigateToEntry, openAddRepositoryEntryDialog],
	);

	const onRelationDialogOpen = useCallback((def: PropertyDefinitionBelongsTo | PropertyDefinitionHas) => {
		setRelationDialogState({ def });
	}, []);

	return {
		anyBlockingDialog,
		hasFieldOrRelationDialog,
		onRelationDialogOpen,
		onAddRelatedEntryClick: handleAddEntryClick,
		onScalarFieldEdit: handleScalarFieldEdit,
		closeFieldEditDialog,
		notifyAddRepositoryEntryCreated,
		closeAddRepositoryEntryDialogWithCleanup,
		editFieldDialogProps,
		relationDialogProps,
	};
}
